import React from 'react'
import ReactDOM from 'react-dom/client'
import GlobalStyles from 'theme/globalStyles'
import theme from 'theme/theme'
import {ThemeProvider} from 'styled-components'
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles'
import * as serviceWorker from './serviceWorker'
import ReduxProvider from 'providers/ReduxProvider'
import App from './App'
import './i18n'
import 'assets/fonts/fonts.css'
import {HTML5Backend} from 'react-dnd-html5-backend'
import {DndProvider} from 'react-dnd'
import {BrowserRouter as Router} from 'react-router-dom'
import { APP_BUILD_ID } from './buildInfo'
import { setOnlineStatusRoutine, updateSyncCountsRoutine, triggerSyncRoutine } from 'ducks/offline/actions'

const AUTO_RELOAD_GUARD_KEY = 'sw_auto_reloaded_for_build'
const LEGACY_AUTO_RELOAD_KEY = 'sw_auto_reloaded'
const FORCED_REFRESH_GUARD_KEY = 'sw_forced_refresh_for_build'

const hasReloadedForCurrentBuild = () =>
  sessionStorage.getItem(AUTO_RELOAD_GUARD_KEY) === APP_BUILD_ID

const markReloadedForCurrentBuild = () => {
  sessionStorage.setItem(AUTO_RELOAD_GUARD_KEY, APP_BUILD_ID)
}

const clearLegacyReloadFlag = () => {
  sessionStorage.removeItem(LEGACY_AUTO_RELOAD_KEY)
}

const getBuildFromScriptUrl = (scriptUrl) => {
  if (!scriptUrl) return null
  try {
    const url = new URL(scriptUrl, window.location.origin)
    return url.searchParams.get('build')
  } catch {
    return null
  }
}

const muiTheme = createTheme({
  palette: {
    primary: { main: theme.colors.secondary, contrastText: '#fff' },
    secondary: { main: theme.colors.primary, contrastText: '#fff' },
  },
  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 600 },
        containedPrimary: { color: '#fff' },
      },
    },
    MuiModal: {
      defaultProps: { disableScrollLock: true },
    },
    MuiPopover: {
      defaultProps: { disableScrollLock: true },
    },
    MuiMenu: {
      defaultProps: { disableScrollLock: true },
    },
    MuiDialog: {
      defaultProps: { disableScrollLock: true },
    },
  },
})

const container = document.getElementById('root')
const root = ReactDOM.createRoot(container)
root.render(
  /* TODO: Check the Strict mode for errors*/
  <React.StrictMode>
    <DndProvider backend={HTML5Backend}>
      <ThemeProvider theme={theme}>
        <MuiThemeProvider theme={muiTheme}>
          <GlobalStyles/>
          <ReduxProvider>
            <Router>
              <App/>
            </Router>
          </ReduxProvider>
        </MuiThemeProvider>
      </ThemeProvider>
    </DndProvider>
  </React.StrictMode>
)


// Offline/online connectivity detection
// window.reduxStore is set in ReduxProvider.js
window.addEventListener('online', () => {
  const store = window.reduxStore
  if (store) {
    store.dispatch(setOnlineStatusRoutine({ isOnline: true }))
    store.dispatch(updateSyncCountsRoutine.trigger())
  }
})

window.addEventListener('offline', () => {
  const store = window.reduxStore
  if (store) {
    store.dispatch(setOnlineStatusRoutine({ isOnline: false }))
  }
})

// Periodic connectivity check — only recovers from stuck offline state
// Runs every 15s (not 5s) to avoid spamming. Only acts on browser online event mismatch.
setInterval(() => {
  const store = window.reduxStore
  if (!store) return

  // Browser says offline but Redux says online → update Redux
  if (!navigator.onLine && store.getState().offline?.isOnline) {
    store.dispatch(setOnlineStatusRoutine({ isOnline: false }))
  }
}, 15000)

serviceWorker.register({
  onSuccess: () => {
    clearLegacyReloadFlag()
    console.log('PWA ready: assets cached for offline usage.')
  },
  onUpdate: (registration) => {
    console.log('New app version available. Updating now...')

    // Avoid infinite reload loops when the SW controller changes.
    const reloaded = hasReloadedForCurrentBuild()
    if (!reloaded) {
      const handleControllerChange = () => {
        if (hasReloadedForCurrentBuild()) return
        markReloadedForCurrentBuild()

        window.location.replace(window.location.href)
      }
      navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange, { once: true })

      setTimeout(() => {
        if (!hasReloadedForCurrentBuild()) {
          markReloadedForCurrentBuild()
          window.location.replace(window.location.href)
        }
      }, 1000)
    }

    if (registration?.waiting) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' })
    }
  }
})

if (process.env.NODE_ENV !== 'production' && 'serviceWorker' in navigator) {
  const wasControlledByServiceWorker = !!navigator.serviceWorker.controller
  Promise.all([
    navigator.serviceWorker.getRegistrations()
      .then(registrations => Promise.all(registrations.map(registration => registration.unregister())))
      .catch(() => []),
    (window.caches && window.caches.keys
      ? window.caches.keys().then(keys => Promise.all(keys.map(key => window.caches.delete(key))))
      : Promise.resolve([])).catch(() => []),
  ]).then(() => {
    if (wasControlledByServiceWorker && !sessionStorage.getItem('dev_sw_evicted')) {
      sessionStorage.setItem('dev_sw_evicted', '1')
      window.location.reload()
    }
  })
}

if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
  clearLegacyReloadFlag()

  // If a previous session stored a different build id, unlock reload for the current build.
  const storedBuild = sessionStorage.getItem(AUTO_RELOAD_GUARD_KEY)
  if (storedBuild && storedBuild !== APP_BUILD_ID) {
    sessionStorage.removeItem(AUTO_RELOAD_GUARD_KEY)
  }

  const applyImmediateUpdate = async () => {
    const registration = await navigator.serviceWorker.getRegistration()
    if (!registration) return

    await registration.update()

    if (registration.waiting) {
      const reloaded = hasReloadedForCurrentBuild()
      if (!reloaded) {
        const handleControllerChange = () => {
          if (hasReloadedForCurrentBuild()) return
          markReloadedForCurrentBuild()

          window.location.replace(window.location.href)
        }
        navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange, { once: true })
      }
      registration.waiting.postMessage({ type: 'SKIP_WAITING' })

      setTimeout(() => {
        if (!hasReloadedForCurrentBuild() && registration.waiting) {
          markReloadedForCurrentBuild()
          window.location.replace(window.location.href)
        }
      }, 1000)

      return
    }
  }

  const checkBuildInfoAndUpdate = async () => {
    try {
      const response = await fetch(
        `${process.env.PUBLIC_URL}/build-info.json?ts=${Date.now()}`,
        { cache: 'no-store' }
      )
      if (!response.ok) return
      const data = await response.json()
      const latestBuildId = data?.buildId ? String(data.buildId) : null
      if (!latestBuildId || latestBuildId === APP_BUILD_ID) return

      const alreadyAppliedFor = sessionStorage.getItem('sw_applied_build_id')
      if (alreadyAppliedFor === latestBuildId) return
      sessionStorage.setItem('sw_applied_build_id', latestBuildId)

      await applyImmediateUpdate()

      const registration = await navigator.serviceWorker.getRegistration()
      if (!registration) return

      const activeBuildId = getBuildFromScriptUrl(registration.active?.scriptURL)
      if (activeBuildId === latestBuildId) return

      if (registration.waiting) return
      const forcedForBuild = sessionStorage.getItem(FORCED_REFRESH_GUARD_KEY)
      if (forcedForBuild === latestBuildId) return

      sessionStorage.setItem(FORCED_REFRESH_GUARD_KEY, latestBuildId)
      await registration.unregister()

      window.location.replace(window.location.href)
    } catch (error) {
      // Silent fail: update checks should never block the app.
    }
  }

  const tryActivateWaitingWorker = (registration) => {
    if (!registration?.waiting) return

    const reloaded = hasReloadedForCurrentBuild()
    if (!reloaded) {
      const handleControllerChange = () => {
        if (hasReloadedForCurrentBuild()) return
        markReloadedForCurrentBuild()

        window.location.replace(window.location.href)
      }
      navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange, { once: true })


      setTimeout(() => {
        if (!hasReloadedForCurrentBuild() && registration.waiting) {
          markReloadedForCurrentBuild()
          window.location.replace(window.location.href)
        }
      }, 1000)
    }

    registration.waiting.postMessage({ type: 'SKIP_WAITING' })
  }

  const checkForUpdates = async () => {
    try {
      await checkBuildInfoAndUpdate()
      const registration = await navigator.serviceWorker.getRegistration()
      if (!registration) return
      await registration.update()
      tryActivateWaitingWorker(registration)
    } catch (error) {
      // Silent fail: this should never block the app.
    }
  }

  checkForUpdates()

  window.addEventListener('focus', checkForUpdates)
  window.addEventListener('pageshow', (event) => {

    if (event.persisted) {

      checkForUpdates()
    } else {
      checkForUpdates()
    }
  })
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {

      checkForUpdates()
    }
  })

  // Periodic check to reduce stale sessions in long-running PWA tabs.
  setInterval(checkForUpdates, 10 * 1000)
}
